// public/js/services/analysis/CoreAnalysisMethods.js

export const CoreAnalysisMethods = {
    async analyzeGames(games, progressCallback) {
        try {
            // Input validation
            this.validateGames(games);

            // Check client-side cache
            const cacheKey = this.generateCacheKey(games);
            const cached = this.getCachedResults(cacheKey);
            if (cached) return cached;

            const totalSteps = 7;
            let currentStep = 0;

            // Process in batches for better browser performance
            const batches = this.createBatches(games, this.options.batchSize);
            const processedResults = [];

            for (const batch of batches) {
                if (this.abortController.signal.aborted) {
                    throw new Error('Analysis cancelled');
                }

                try {
                    progressCallback?.(++currentStep, totalSteps, `Processing ${batch.length} games...`);
                    const batchResults = await Promise.all(
                        batch.map(game => this.analyzeGame(game))
                    );
                    processedResults.push(...batchResults.filter(Boolean));
                } catch (error) {
                    console.error('Error processing batch:', error);
                }
            }

            const results = {
                summary: this.calculateSummary(processedResults),
                trends: this.identifyTrends(processedResults),
                recommendations: this.generateRecommendations(processedResults),
                gameAnalyses: processedResults,
                metadata: {
                    analyzedAt: new Date().toISOString(),
                    gameCount: games.length,
                    cacheKey
                }
            };

            // Cache the results
            this.setCachedResults(cacheKey, results);
            return results;

        } catch (error) {
            console.error('Error analyzing games:', error);
            throw new Error(`Analysis failed: ${error.message}`);
        }
    },

    async analyzeGame(game) {
        try {
            // Fetch additional data if needed
            const [details, wishlistData] = await Promise.all([
                this.fetchGameDetails(game.steamId),
                this.fetchWishlistData(game.steamId)
            ]);

            return {
                gameId: game.steamId,
                name: game.name,
                insights: {
                    wishlistTrends: this.analyzeWishlistTrends(wishlistData),
                    growthMetrics: this.calculateGrowthMetrics(details),
                    marketPosition: this.assessMarketPosition(game, details)
                }
            };
        } catch (error) {
            console.error(`Error analyzing game ${game.name}:`, error);
            return null;
        }
    },

    calculateSummary(games) {
        const totalRevenue = this.sum(games.map(g => g.revenue));
        const averageRevenue = totalRevenue / games.length;
        const totalPlayers = this.sum(games.map(g => g.players));
        const totalWishlists = this.sum(games.map(g => g.wishlists));
        const totalSales = this.sum(games.map(g => g.copiesSold));

        return {
            totalGames: games.length,
            averageRevenue,
            totalRevenue,
            totalPlayers,
            totalWishlists,
            totalSales,
            wishlistConversion: totalWishlists > 0 ? (totalSales / totalWishlists) * 100 : 0,
            averageReviewScore: this.calculateAverage(games.map(g => g.reviewScore)),
            trends: this.calculateOverallTrends(games)
        };
    },

    identifyTrends(games) {
        return {
            rising: games.filter(g => this.isRising(g)),
            trending: games.filter(g => this.isTrending(g)),
            potential: games.filter(g => this.hasPotential(g)),
            metrics: {
                averageGrowth: this.calculateAverageGrowth(games),
                growthDistribution: this.calculateGrowthDistribution(games),
                marketTrends: this.identifyMarketTrends(games)
            }
        };
    },

    generateRecommendations(games) {
        return {
            highPotential: this.identifyHighPotentialGames(games),
            needsAttention: this.identifyGamesNeedingAttention(games),
            pricing: this.generatePricingRecommendations(games),
            marketing: this.generateMarketingRecommendations(games),
            development: this.generateDevelopmentRecommendations(games)
        };
    },

    validateGames(games) {
        if (!Array.isArray(games) || games.length === 0) {
            throw new Error('Invalid input: games must be a non-empty array');
        }

        const requiredFields = ['steamId', 'name', 'publishers'];
        const missingFields = games.some(game =>
            requiredFields.some(field => !game.hasOwnProperty(field))
        );

        if (missingFields) {
            throw new Error('Invalid game data: missing required fields');
        }
    }
};